import React from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Link, type SxProps, type Theme, Typography } from '@mui/material';
import Image from 'next/image';

/** Display face for the sign-in heading, matching the 2026 website (loaded in _document). Everything else stays Inter. */
export const AUTH_DISPLAY_FONT = "'Manrope', Inter, ui-sans-serif, system-ui, sans-serif";

/** Deep blue from the website's "Arctic ice" ramp; cyan on white is too light for small text. */
export const AUTH_LINK_COLOR = '#0A6BE8';

const WEBSITE = 'https://www.skript.com.au';

/**
 * Shared chrome for the unauthenticated / onboarding screens: a clean white page with a slim top
 * bar (logo left, a subtle "Secure …" pill right) and a centred content column.
 *
 * `variant="card"` (SKD-2375) is the sign-in look taken from the website: a soft cyan glow behind a
 * white card with the ice ramp along its top edge, an optional trust row under the card and a
 * footer with the website's Privacy / Terms links. The default `plain` variant is unchanged and
 * is what the sign-up flow uses.
 */
export default function AuthLayout({
  children,
  maxWidth = 460,
  badge = 'Secure sign-up',
  variant = 'plain',
  trust,
}: {
  children: React.ReactNode;
  maxWidth?: number;
  /** Text of the lock pill in the top bar. */
  badge?: string;
  variant?: 'plain' | 'card';
  /** Short claims shown under the card, e.g. "CDR-accredited". Card variant only. */
  trust?: string[];
}): JSX.Element {
  const card = variant === 'card';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        ...(card
          ? {
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '50%',
                top: '-140px',
                width: '900px',
                height: '520px',
                transform: 'translateX(-50%)',
                borderRadius: '50%',
                background:
                  'radial-gradient(closest-side, rgba(5,189,255,0.16), rgba(10,132,255,0.06) 55%, transparent 100%)',
                filter: 'blur(40px)',
                pointerEvents: 'none',
              },
            }
          : {}),
      }}>
      <Box
        component="header"
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 3, sm: 6 },
          py: 2.5,
          borderBottom: card ? 'none' : '1px solid #EEF2F7',
        }}>
        <Image src="/images/skriptLogo.svg" width={104} height={40} alt="Skript" priority />
        <AuthPill>{badge}</AuthPill>
      </Box>

      <Box
        component="main"
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3.5,
          px: 2,
          py: card ? { xs: 3, sm: 6 } : 6,
        }}>
        {card ? (
          <Box
            sx={{
              width: '100%',
              maxWidth: `${maxWidth}px`,
              boxSizing: 'border-box',
              p: { xs: '28px 20px', sm: 5 },
              backgroundColor: '#ffffff',
              border: { xs: 'none', sm: '1px solid #DCE3ED' },
              borderRadius: '20px',
              boxShadow: { xs: 'none', sm: '0 24px 56px -24px rgba(9,18,83,0.22), 0 4px 12px rgba(9,18,83,0.06)' },
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #7BDAFF 0%, #05BDFF 50%, #0A84FF 100%)',
              },
            }}>
            {children}
          </Box>
        ) : (
          <Box sx={{ width: '100%', maxWidth: `${maxWidth}px` }}>{children}</Box>
        )}

        {card && trust != null && trust.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2.25,
              fontSize: '13px',
              fontWeight: 500,
              color: '#5B6B80',
            }}>
            {trust.map((item) => (
              <Box key={item} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '999px',
                    backgroundColor: '#05BDFF',
                    boxShadow: '0 0 8px #05BDFF',
                  }}
                />
                {item}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {card && <AuthFooter />}
    </Box>
  );
}

/** The lock pill in the top bar ("Secure sign-in", "Secure sign-up", …). */
export function AuthPill({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        border: '1px solid #DCE3ED',
        borderRadius: '999px',
        px: 1.75,
        py: 0.75,
        color: '#3E4B5E',
        fontSize: '13px',
        fontWeight: 500,
        backgroundColor: '#ffffff',
      }}>
      <LockOutlinedIcon sx={{ fontSize: 15, color: '#05BDFF' }} />
      {children}
    </Box>
  );
}

/** The © line with the website's Privacy / Terms / Contact links. */
export function AuthFooter(): JSX.Element {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        px: { xs: 3, sm: 6 },
        pb: 3,
        fontSize: '12px',
        color: '#8796AA',
      }}>
      <span>© Skript Pty Ltd</span>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Link href={`${WEBSITE}/privacy-policy/`} sx={{ color: '#5B6B80', textDecoration: 'none' }}>
          Privacy
        </Link>
        <Link href={`${WEBSITE}/terms-of-use/`} sx={{ color: '#5B6B80', textDecoration: 'none' }}>
          Terms
        </Link>
        <Link href={`${WEBSITE}/contact/`} sx={{ color: '#5B6B80', textDecoration: 'none' }}>
          Contact
        </Link>
      </Box>
    </Box>
  );
}

/** The soft cyan glow behind the auth pages, as a `::before` on a `position: relative` root. */
export const authGlowSx = {
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: '-140px',
    width: '900px',
    height: '520px',
    transform: 'translateX(-50%)',
    borderRadius: '50%',
    background: 'radial-gradient(closest-side, rgba(5,189,255,0.16), rgba(10,132,255,0.06) 55%, transparent 100%)',
    filter: 'blur(40px)',
    pointerEvents: 'none',
  },
} as const;

/** The white card with the Arctic-ice line along its top, as sx for any Box / Paper. */
export const authCardSx = {
  backgroundColor: '#ffffff',
  border: '1px solid #DCE3ED',
  borderRadius: '20px',
  boxShadow: '0 24px 56px -24px rgba(9,18,83,0.22), 0 4px 12px rgba(9,18,83,0.06)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #7BDAFF 0%, #05BDFF 50%, #0A84FF 100%)',
  },
} as const;

/** Uppercase, letter-spaced eyebrow label used above headings. */
export function Eyebrow({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Typography
      sx={{
        color: AUTH_LINK_COLOR,
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        mb: 1.5,
      }}>
      {children}
    </Typography>
  );
}

/**
 * Label row above a field: the label on the left and an optional action (e.g. "Forgot
 * password?") on the right, so the field itself carries no floating label.
 */
export function FieldLabel({
  htmlFor,
  children,
  action,
}: {
  htmlFor: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, mb: 1 }}>
      <Typography
        component="label"
        htmlFor={htmlFor}
        sx={{ fontSize: '13px', fontWeight: 600, color: '#3E4B5E', letterSpacing: '0.02em' }}>
        {children}
      </Typography>
      {action}
    </Box>
  );
}

/** Field styling for the card variant: 48px tall, slate fill, cyan focus ring. */
export const authFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: '48px',
    backgroundColor: '#F6F8FB',
    borderRadius: '10px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DCE3ED' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B9C4D2' },
    '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(5,189,255,0.16)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(5,189,255,0.7)', borderWidth: '1px' },
  },
  '& .MuiOutlinedInput-input': { fontSize: '15px', color: '#091115' },
  '& .MuiFormHelperText-root': { mx: 0, mt: 0.75 },
};

/** The website's cyan → blue gradient button with its soft glow. */
export const authPrimaryButtonSx: SxProps<Theme> = {
  height: '50px',
  borderRadius: '10px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#ffffff',
  background: 'linear-gradient(120deg, #05BDFF 0%, #0A6BE8 100%)',
  boxShadow: '0 8px 28px -10px rgba(5,189,255,0.65), 0 6px 28px -8px rgba(10,107,232,0.45)',
  '&:hover': {
    background: 'linear-gradient(120deg, #05BDFF 0%, #0A6BE8 100%)',
    filter: 'brightness(1.08)',
    boxShadow: '0 8px 28px -10px rgba(5,189,255,0.65), 0 6px 28px -8px rgba(10,107,232,0.45)',
  },
  '&.Mui-disabled': { color: '#ffffff', opacity: 0.7 },
};

/** Eye icon button that shows / hides the password, sitting inside the field. */
export const authToggleButtonSx: SxProps<Theme> = {
  width: '36px',
  height: '36px',
  // edge="end" pulls the button 12px into the field's padding; keep 5px of that clear.
  mr: '-7px',
  color: '#5B6B80',
  borderRadius: '8px',
  '& svg': { fontSize: '20px' },
  '&:hover': { backgroundColor: '#EEF2F7', color: '#091115' },
  '@media (max-width:899.95px)': { minWidth: '36px', minHeight: '36px' },
};
